import type { CombinedReading } from '../ai/prompts';
import { COLOR_INTROS, findColorClusters } from '../data/color-intros';
import { tEn, tZh } from '../i18n';
import type { ReadingState } from '../types';
import { escapeHtml } from './escape';
import { MAX_CLARIFIERS } from './reveal';

export interface SynthesisActions {
  onDrawAgain(): void;
  onClarify(): void;
  onDone(): void;
}

export function mountSynthesisFooter(
  container: HTMLElement,
  state: ReadingState,
  actions: SynthesisActions,
): void {
  container.style.display = 'block';
  container.innerHTML = `
    <div class="thread-title">
      <span lang="en"><em>${tEn('thread.title')}</em></span>
      <span lang="zh">${tZh('thread.title')}</span>
    </div>
    <div id="cluster-banners">${renderClusterBanners(state)}</div>
    <div class="synthesis-loading" id="synth-loading" style="display:none">
      <div class="ripple-stage">
        <div class="ripple-ring"></div><div class="ripple-ring"></div>
        <div class="ripple-ring"></div><div class="ripple-core"></div>
      </div>
      <div class="loading-line">
        <span lang="en">${tEn('thread.loading')}</span>
        <span lang="zh">${tZh('thread.loading')}</span>
      </div>
    </div>
    <div class="synthesis-text" id="synth-text"></div>
    <div class="reflection-prompt" id="synth-reflect"></div>
    <div class="reading-actions">
      <button class="act-btn primary" id="act-draw"><span lang="en">${tEn('actions.draw_again')}</span><span lang="zh">${tZh('actions.draw_again')}</span></button>
      <button class="act-btn" id="act-clarify"><span lang="en">${tEn('actions.clarify')}</span><span lang="zh">${tZh('actions.clarify')}</span> <span class="cap-count" id="cap-count">0/${MAX_CLARIFIERS}</span></button>
      <button class="act-btn subtle" id="act-done"><span lang="en">${tEn('actions.done')}</span><span lang="zh">${tZh('actions.done')}</span></button>
    </div>
    <div class="ai-state" id="ai-state"></div>
  `;
  container.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Draw-again and Done navigate away — guard against double-clicks racing the
  // dispose. Clarify can be clicked multiple times (each call draws another
  // clarifier card and pullClarifier has its own guard).
  const once = (fn: () => void): (() => void) => {
    let fired = false;
    return () => {
      if (fired) return;
      fired = true;
      fn();
    };
  };
  container.querySelector('#act-draw')?.addEventListener('click', once(actions.onDrawAgain));
  container.querySelector('#act-clarify')?.addEventListener('click', actions.onClarify);
  container.querySelector('#act-done')?.addEventListener('click', once(actions.onDone));

  if (state.cachedReading) {
    void composeSynthesisFromCache(container, state.cachedReading);
  } else {
    renderFallback(container);
  }
}

async function composeSynthesisFromCache(
  container: HTMLElement,
  promise: Promise<CombinedReading>,
): Promise<void> {
  const loading = container.querySelector('#synth-loading') as HTMLElement;
  const synth = container.querySelector('#synth-text') as HTMLElement;
  const reflect = container.querySelector('#synth-reflect') as HTMLElement;
  const stateEl = container.querySelector('#ai-state') as HTMLElement;

  loading.style.display = 'flex';
  synth.innerHTML = '';
  reflect.innerHTML = '';
  stateEl.innerHTML = '';

  try {
    const reading = await promise;
    loading.style.display = 'none';
    applySynthesis(container, reading);
  } catch {
    loading.style.display = 'none';
    renderFallback(container);
  }
}

export function renderFallback(container: HTMLElement): void {
  const synth = container.querySelector('#synth-text') as HTMLElement;
  const refEl = container.querySelector('#synth-reflect') as HTMLElement;
  const stateEl = container.querySelector('#ai-state') as HTMLElement;
  synth.innerHTML = `<span lang="en"><em>${tEn('thread.fallback')}</em></span><span lang="zh">${tZh('thread.fallback')}</span>`;
  refEl.innerHTML = `<span lang="en"><em>${tEn('thread.reflection')}</em></span><span lang="zh">${tZh('thread.reflection')}</span>`;
  stateEl.innerHTML = `<span lang="en">${tEn('ai.state_static')}</span><span lang="zh">${tZh('ai.state_static')}</span>`;
}

/**
 * Re-render the cluster banners after the spread changes (clarifier draws
 * mutate state.cards). Called from the clarifier flow once the new card
 * has landed in state.
 */
export function refreshClusterBanners(container: HTMLElement, state: ReadingState): void {
  const slot = container.querySelector<HTMLElement>('#cluster-banners');
  if (slot) slot.innerHTML = renderClusterBanners(state);
}

/**
 * Renders one color-banner per detected cluster (≥2 cards of the same
 * color in the current spread). Empty string when no clusters — the
 * banner section disappears entirely. Recomputed from `state.cards`,
 * which the clarifier flow mutates as new cards are pulled.
 */
function renderClusterBanners(state: ReadingState): string {
  const clusters = findColorClusters(state.cards);
  if (clusters.length === 0) return '';
  return clusters
    .map(({ category, count }) => {
      const intro = COLOR_INTROS[category];
      return `<div class="color-banner" data-category="${category}">
        <span lang="en"><em>${escapeHtml(category.toUpperCase())} ×${count} — ${escapeHtml(intro.en)}</em></span>
        <span lang="zh">${escapeHtml(category.toUpperCase())} ×${count} — ${escapeHtml(intro.zh)}</span>
      </div>`;
    })
    .join('');
}

/** Toggle the synthesis-loading ripple. Used by the clarifier flow to
 *  signal that the footer is being re-composed in the background.
 *  When `weaving` is true, the loading caption switches to the clarifier
 *  copy ("正在融入新卦……") so users see this is an UPDATE in progress,
 *  not the original first-time load. */
export function setSynthesisLoading(
  container: HTMLElement,
  loading: boolean,
  weaving = false,
): void {
  const el = container.querySelector<HTMLElement>('#synth-loading');
  if (el) el.style.display = loading ? 'flex' : 'none';
  const line = container.querySelector<HTMLElement>('#synth-loading .loading-line');
  if (line && loading) {
    const key = weaving ? 'thread.weaving' : 'thread.loading';
    line.innerHTML = `<span lang="en">${tEn(key)}</span><span lang="zh">${tZh(key)}</span>`;
  }
}

/** Replace the rendered synthesis + reflection text from a CombinedReading.
 *  Called by the clarifier flow once the new (integrated) synthesis arrives. */
export function applySynthesis(container: HTMLElement, reading: CombinedReading): void {
  const synth = container.querySelector<HTMLElement>('#synth-text');
  const reflect = container.querySelector<HTMLElement>('#synth-reflect');
  const stateEl = container.querySelector<HTMLElement>('#ai-state');
  if (synth)
    synth.innerHTML = `<span lang="en"><em>${escapeHtml(reading.synthesis_en)}</em></span><span lang="zh">${escapeHtml(reading.synthesis_zh)}</span>`;
  if (reflect)
    reflect.innerHTML = `<span lang="en"><em>${escapeHtml(reading.reflection_en)}</em></span><span lang="zh">${escapeHtml(reading.reflection_zh)}</span>`;
  if (stateEl)
    stateEl.innerHTML = `<span lang="en">${tEn('thread.composed_by')}</span><span lang="zh">${tZh('thread.composed_by')}</span>`;
}
